/*
 * A3DP blog landing motion layer — self-contained, no dependencies.
 *
 * What it does when armed:
 *   1. Subtle scroll parallax on article-card images that are BELOW the fold
 *      at load: translate3d + compensating scale on the <img>, clipped by the
 *      card's overflow-hidden media box. Above-the-fold cards are never
 *      touched, so the LCP element is unmodified (no pop, no reflow).
 *   2. Decorative parallax accent behind the category bar (sets
 *      --a3dp-accent-y, consumed by a3dp-blog-motion.css).
 *   3. Card hover lift is CSS-only (see a3dp-blog-motion.css).
 *
 * Guarantees:
 *   - Transform-only; no layout reads/writes interleaved (batch reads, then
 *     writes, once per animation frame).
 *   - Passive scroll listener; at most one rAF in flight.
 *   - Arms only on fine-pointer + >=750px viewports with
 *     prefers-reduced-motion not set. Otherwise the page stays fully static.
 *   - Without JS the page is identical to the static layout.
 */
(function () {
  'use strict';

  var cfgEl = document.querySelector('script[data-a3dp-motion]');
  if (!cfgEl) return;
  var cfg = {};
  try {
    cfg = JSON.parse(cfgEl.getAttribute('data-a3dp-motion') || '{}');
  } catch (err) {
    cfg = {};
  }
  if (cfg.enable === false) return;

  var DEPTH = Math.min(Math.max(parseInt(cfg.depth, 10) || 10, 4), 24);
  var USE_ACCENT = cfg.accent !== false;

  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqFine = window.matchMedia('(hover: hover) and (pointer: fine)');
  var mqWide = window.matchMedia('(min-width: 750px)');

  var armed = false;
  var cards = [];
  var bar = null;
  var pendingFrame = false;
  var resizeTimer = 0;

  /* hover lift is CSS-driven; JS only toggles the opt-out class */
  function applyHoverPref() {
    document.documentElement.classList.toggle(
      'a3dp-motion--no-hover',
      armed && cfg.hover === false
    );
  }

  function allowed() {
    return !mqReduce.matches && mqFine.matches && mqWide.matches;
  }

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  /* progress of an element through the viewport: -1 = leaving at top,
     0 = centered, +1 = entering from bottom */
  function progress(top, height, vh) {
    return clamp((top + height / 2 - vh / 2) / ((vh + height) / 2), -1, 1);
  }

  function collect() {
    cards = [];
    var nodes = document.querySelectorAll('.blog-articles__article');
    for (var i = 0; i < nodes.length; i++) {
      var img = nodes[i].querySelector('.article-card__image.media > img');
      if (!img) continue; /* text-only card: nothing to parallax */
      cards.push({ el: nodes[i], img: img, parallax: false, scale: 1, baseH: 0 });
    }
    bar = document.querySelector('.blog-category-bar');
  }

  function measure() {
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      if (!c.parallax) continue;
      var r = c.img.getBoundingClientRect();
      var h = c.scale > 1 ? r.height / c.scale : r.height;
      if (h > 0) {
        c.baseH = h;
        c.scale = 1 + (2 * DEPTH) / h + 0.02;
      }
    }
  }

  function update() {
    pendingFrame = false;
    if (!armed) return;
    var vh = window.innerHeight;

    /* reads first */
    var reads = [];
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      if (!c.parallax) continue;
      var r = c.el.getBoundingClientRect();
      reads.push({ c: c, p: progress(r.top, r.height, vh) });
    }
    var barP = 0;
    if (bar && USE_ACCENT) {
      var br = bar.getBoundingClientRect();
      barP = progress(br.top, br.height, vh);
    }

    /* then writes */
    for (var j = 0; j < reads.length; j++) {
      var e = reads[j];
      var y = (-e.p * DEPTH).toFixed(2);
      e.c.img.style.transform =
        'translate3d(0,' + y + 'px,0) scale(' + e.c.scale.toFixed(4) + ')';
    }
    if (bar && USE_ACCENT) {
      bar.style.setProperty('--a3dp-accent-y', (-barP * DEPTH * 0.5).toFixed(2));
    }
  }

  function schedule() {
    if (!pendingFrame) {
      pendingFrame = true;
      window.requestAnimationFrame(update);
    }
  }

  function arm() {
    if (armed) return;
    armed = true;
    document.documentElement.classList.add('a3dp-motion');
    applyHoverPref();
    collect();
    var vh = window.innerHeight;
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      var r = c.el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) continue; /* above the fold: stay static, LCP-safe */
      var imgR = c.img.getBoundingClientRect();
      c.baseH = imgR.height;
      c.scale = imgR.height > 0 ? 1 + (2 * DEPTH) / imgR.height + 0.02 : 1.1;
      c.parallax = true;
      c.el.classList.add('a3dp-px');
      /* apply the initial transform now, while the card is offscreen — no pop */
      var p = progress(r.top, r.height, vh);
      c.img.style.transform =
        'translate3d(0,' + (-p * DEPTH).toFixed(2) + 'px,0) scale(' + c.scale.toFixed(4) + ')';
    }
    if (bar && USE_ACCENT) {
      var br = bar.getBoundingClientRect();
      bar.style.setProperty('--a3dp-accent-y', (-progress(br.top, br.height, vh) * DEPTH * 0.5).toFixed(2));
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
  }

  function disarm() {
    if (!armed) return;
    armed = false;
    document.documentElement.classList.remove('a3dp-motion');
    document.documentElement.classList.remove('a3dp-motion--no-hover');
    for (var i = 0; i < cards.length; i++) {
      cards[i].img.style.transform = '';
      cards[i].el.classList.remove('a3dp-px');
      cards[i].parallax = false;
    }
    if (bar) bar.style.removeProperty('--a3dp-accent-y');
  }

  function onMediaChange() {
    if (allowed() && !armed) arm();
    else if (!allowed() && armed) disarm();
  }

  /* debounce re-measure on resize (image box heights change) */
  window.addEventListener('resize', function () {
    if (!armed) return;
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      measure();
      schedule();
    }, 150);
  });

  /* theme editor re-renders sections: re-collect */
  document.addEventListener('shopify:section:load', function () {
    if (!armed) return;
    disarm();
    if (allowed()) arm();
  });

  if (allowed()) arm();

  /* media-query change handling is best-effort: registration must never
     prevent arming (worst case, effects stay in their initial state until
     the next full page load) */
  if (mqReduce.addEventListener) {
    try {
      mqReduce.addEventListener('change', onMediaChange);
      mqFine.addEventListener('change', onMediaChange);
      mqWide.addEventListener('change', onMediaChange);
    } catch (err) { /* no dynamic re-arming on this browser */ }
  }
})();
